import { ChevronRight, Loader2, Search } from "lucide-react";

import { UpcomingLawDetail } from "@/components/legislation_monitor/UpcomingLawDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaginatedTableControls } from "@/components/ui/paginated-table";
import type { useLegislationMonitorSelection } from "@/hooks/legislationMonitor/useLegislationMonitorSelection";
import { useUpcomingLawsSearch } from "@/hooks/legislationMonitor/useUpcomingLawsSearch";
import {
  formatUpcomingLawDisplayName,
  legislationMonitorEmptyListMessage,
} from "@/lib/legislationMonitorFormat";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";

type UpcomingLawsMonitorPanelProps = ReturnType<typeof useLegislationMonitorSelection>;

export function UpcomingLawsMonitorPanel({
  municipality,
  sessionStates,
  selectedState,
  selectedLawId,
  setSelectedLawId,
}: UpcomingLawsMonitorPanelProps) {
  const {
    query,
    setQuery,
    setPage,
    laws,
    pagination,
    loading,
    error,
  } = useUpcomingLawsSearch(selectedState);

  if (selectedLawId != null) {
    return (
      <UpcomingLawDetail
        lawId={selectedLawId}
        municipalityId={municipality?.id ?? null}
        municipalityName={formatMunicipalityLabel(municipality) || "your municipality"}
        onBack={() => setSelectedLawId(null)}
      />
    );
  }

  return (
    <div className="space-y-0">
      {selectedState != null ? (
        <div className="flex flex-col gap-3 border-b bg-background pb-4 sm:flex-row sm:items-center sm:gap-4">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by bill number or name…"
              className="pl-9"
              aria-label="Search upcoming laws"
            />
          </div>

          {!loading && pagination.total_count > 0 ? (
            <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground">
              {pagination.total_count} Upcoming {selectedState}{" "}
              {pagination.total_count === 1 ? "Law" : "Laws"}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />
            Loading upcoming laws…
          </div>
        ) : error ? (
          <p className="py-12 text-center text-muted-foreground">{error}</p>
        ) : laws.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">
            {legislationMonitorEmptyListMessage({
              sessionStates,
              municipalityName: municipality?.name,
              municipalityState: municipality?.state,
              selectedState,
              query,
            })}
          </p>
        ) : (
          <ul className="space-y-3">
            {laws.map((law) => {
              const displayName = formatUpcomingLawDisplayName(law.name);
              return (
                <li key={law.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-between gap-4 rounded-lg border bg-card p-0 text-left shadow-sm hover:bg-card hover:shadow-md"
                    onClick={() => setSelectedLawId(law.id)}
                  >
                    <div className="flex w-full items-center justify-between gap-4 p-4">
                      <div className="min-w-0 space-y-1">
                        <p className="font-semibold text-foreground">{displayName.primary}</p>
                        <p className="line-clamp-2 text-sm text-muted-foreground">
                          {law.description?.trim() || "No summary available"}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                    </div>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && laws.length > 0 && selectedState != null ? (
          <PaginatedTableControls
            variant="compact"
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            perPage={pagination.per_page}
            totalCount={pagination.total_count}
            onPageChange={setPage}
          />
        ) : null}
      </div>
    </div>
  );
}
